/**
 * ============================================================================
 * DSAR SERVICE - Phase 3
 * ============================================================================
 * Data Subject Access Request automation for GDPR compliance
 *
 * Features:
 * - Self-service DSAR requests
 * - Automated data collection
 * - Secure encrypted export
 * - Approval workflow
 * - Email notifications
 *
 * @pattern Phase 4 - IService + ServiceResponse + typedSupabase
 * @version 1.0.0
 * @date 2026-02-20
 */

import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError } from "./service-registry";
import { logger } from "../lib/logger";


// ============================================================================
// TYPES
// ============================================================================

export interface DSARRequest {
  id: string;
  organization_id: string;
  requester_user_id: string;
  requester_email: string;
  requester_name: string;
  request_type: "access" | "export" | "deletion";
  status: "pending" | "processing" | "completed" | "failed" | "approved" | "rejected";
  description: string | null;
  requested_data_types: string[];
  start_date: string | null;
  end_date: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  export_file_url: string | null;
  export_file_size_bytes: number | null;
  export_expires_at: string | null;
  encryption_key_hash: string | null;
  records_included: Record<string, number> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateDSARRequestParams {
  requesterUserId: string;
  requesterEmail: string;
  requesterName: string;
  requestType: "access" | "export" | "deletion";
  description?: string;
  requestedDataTypes?: string[];
  startDate?: string;
  endDate?: string;
}

export interface ApproveDSARParams {
  requestId: string;
  approverId: string;
}

export interface RejectDSARParams {
  requestId: string;
  approverId: string;
  rejectionReason: string;
}

export interface ProcessDSARParams {
  requestId: string;
  encryptionKey?: string;
}

export interface DSARExportData {
  user_id: string;
  collected_at: string;
  start_date: string | null;
  end_date: string | null;
  task_activities: unknown[];
  sales_activities: unknown[];
  organization_activities: unknown[];
  counts: {
    task_activities: number;
    sales_activities: number;
    organization_activities: number;
  };
}

// ============================================================================
// SERVICE INTERFACE
// ============================================================================

export interface IDSARService extends IService {
  /**
   * Create a new DSAR request
   */
  createRequest(params: CreateDSARRequestParams): Promise<ServiceResponse<DSARRequest>>;

  /**
   * Get DSAR requests for a user
   */
  getUserRequests(userId: string): Promise<ServiceResponse<DSARRequest[]>>;

  /**
   * Get pending DSAR requests for an organization (admin only)
   */
  getPendingRequests(organizationId: string): Promise<ServiceResponse<DSARRequest[]>>;

  /**
   * Get a specific DSAR request
   */
  getRequest(requestId: string): Promise<ServiceResponse<DSARRequest>>;

  /**
   * Approve a DSAR request
   */
  approveRequest(params: ApproveDSARParams): Promise<ServiceResponse<DSARRequest>>;

  /**
   * Reject a DSAR request
   */
  rejectRequest(params: RejectDSARParams): Promise<ServiceResponse<DSARRequest>>;

  /**
   * Process an approved DSAR request
   */
  processRequest(params: ProcessDSARParams): Promise<ServiceResponse<DSARRequest>>;

  /**
   * Collect user activity data
   */
  collectUserData(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ServiceResponse<DSARExportData>>;

  /**
   * Get DSAR export data (if available)
   */
  getExportData(requestId: string): Promise<ServiceResponse<string>>;
}

// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================

export class DSARService implements IDSARService {
  readonly name = "DSARService";

  async initialize(): Promise<void> {
    logger.info("✅ DSARService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("🧹 DSARService disposed");
  }

  async createRequest(params: CreateDSARRequestParams): Promise<ServiceResponse<DSARRequest>> {
    const {
      requesterUserId,
      requesterEmail,
      requesterName,
      requestType,
      description,
      requestedDataTypes = ["activities", "profile", "tasks", "sales"],
      startDate,
      endDate,
    } = params;

    logger.debug("Creating DSAR request", { requesterUserId, requestType });

    try {
      const { data, error } = await typedSupabase.rpc("create_dsar_request", {
        p_requester_user_id: requesterUserId,
        p_requester_email: requesterEmail,
        p_requester_name: requesterName,
        p_request_type: requestType,
        p_description: description || null,
        p_requested_data_types: requestedDataTypes,
        p_start_date: startDate || null,
        p_end_date: endDate || null,
      });

      if (error) {
        return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
      }

      // Fetch the created request
      const requestResponse = await this.getRequest(data as string);
      if (!requestResponse.success) {
        return requestResponse;
      }

      return { success: true, data: requestResponse.data };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
    }
  }

  async getUserRequests(userId: string): Promise<ServiceResponse<DSARRequest[]>> {
    logger.debug("Fetching user DSAR requests", { userId });

    try {
      const { data, error } = await typedSupabase
        .from("dsar_requests" as any)
        .select("*")
        .eq("requester_user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
      }

      return { success: true, data: (data as DSARRequest[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
    }
  }

  async getPendingRequests(organizationId: string): Promise<ServiceResponse<DSARRequest[]>> {
    logger.debug("Fetching pending DSAR requests", { organizationId });

    try {
      const { data, error } = await typedSupabase
        .from("dsar_requests" as any)
        .select("*")
        .eq("organization_id", organizationId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, error } };
      }

      return { success: true, data: (data as DSARRequest[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { organizationId, error } };
    }
  }

  async getRequest(requestId: string): Promise<ServiceResponse<DSARRequest>> {
    logger.debug("Fetching DSAR request", { requestId });

    try {
      const { data, error } = await typedSupabase
        .from("dsar_requests" as any)
        .select("*")
        .eq("id", requestId)
        .single();

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { requestId, error } };
      }

      return { success: true, data: data as DSARRequest };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { requestId, error } };
    }
  }

  async approveRequest(params: ApproveDSARParams): Promise<ServiceResponse<DSARRequest>> {
    const { requestId, approverId } = params;

    logger.debug("Approving DSAR request", { requestId, approverId });

    try {
      const { data, error } = await typedSupabase.rpc("approve_dsar_request", {
        p_request_id: requestId,
        p_approver_id: approverId,
      });

      if (error) {
        return { success: false, error: error.message, code: "APPROVE_FAILED", service: this.name, context: { params, error } };
      }

      // Fetch the updated request
      const requestResponse = await this.getRequest(requestId);
      if (!requestResponse.success) {
        return requestResponse;
      }

      // Automatically process the approved request
      await this.processRequest({ requestId });

      return { success: true, data: requestResponse.data };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "APPROVE_FAILED", service: this.name, context: { params, error } };
    }
  }

  async rejectRequest(params: RejectDSARParams): Promise<ServiceResponse<DSARRequest>> {
    const { requestId, approverId, rejectionReason } = params;

    logger.debug("Rejecting DSAR request", { requestId, approverId });

    try {
      const { data, error } = await typedSupabase.rpc("reject_dsar_request", {
        p_request_id: requestId,
        p_approver_id: approverId,
        p_rejection_reason: rejectionReason,
      });

      if (error) {
        return { success: false, error: error.message, code: "REJECT_FAILED", service: this.name, context: { params, error } };
      }

      // Fetch the updated request
      const requestResponse = await this.getRequest(requestId);
      if (!requestResponse.success) {
        return requestResponse;
      }

      return { success: true, data: requestResponse.data };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "REJECT_FAILED", service: this.name, context: { params, error } };
    }
  }

  async processRequest(params: ProcessDSARParams): Promise<ServiceResponse<DSARRequest>> {
    const { requestId, encryptionKey } = params;

    logger.debug("Processing DSAR request", { requestId });

    try {
      // Update status to processing
      await typedSupabase.rpc("update_dsar_request_status", {
        p_request_id: requestId,
        p_status: "processing",
      });

      // Get request details
      const requestResponse = await this.getRequest(requestId);
      if (!requestResponse.success || !requestResponse.data) {
        return { success: false, error: "DSAR request not found", code: "REQUEST_NOT_FOUND", service: this.name, context: { requestId } };
      }

      const request = requestResponse.data;

      // Collect user data
      const dataResponse = await this.collectUserData(
        request.requester_user_id,
        request.start_date || undefined,
        request.end_date || undefined
      );

      if (!dataResponse.success || !dataResponse.data) {
        // Update status to failed
        await typedSupabase.rpc("update_dsar_request_status", {
          p_request_id: requestId,
          p_status: "failed",
          p_error_message: dataResponse.error || "Failed to collect data",
        });

        return { success: false, error: "Failed to collect user data", code: "PROCESSING_FAILED", service: this.name, context: { requestId } };
      }

      // For now, store the data as JSON (in production, this would be uploaded to S3 with encryption)
      const exportData = JSON.stringify(dataResponse.data, null, 2);
      const exportSize = new TextEncoder().encode(exportData).length;

      // Generate encryption key hash (for verification)
      let keyHash: string | null = null;
      if (encryptionKey) {
        const { createHash } = await import("crypto");
        keyHash = createHash("sha256").update(encryptionKey).digest("hex");
      }

      // Calculate expiration (30 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      // Update request with export details
      await typedSupabase.rpc("update_dsar_request_status", {
        p_request_id: requestId,
        p_status: "completed",
        p_export_file_url: `dsar-export-${requestId}.json`, // In production, this would be an S3 URL
        p_export_file_size_bytes: exportSize,
        p_export_expires_at: expiresAt.toISOString(),
        p_encryption_key_hash: keyHash,
        p_records_included: dataResponse.data.counts,
      });

      // Fetch the updated request
      const updatedResponse = await this.getRequest(requestId);
      return updatedResponse;
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");

      // Update status to failed
      await typedSupabase.rpc("update_dsar_request_status", {
        p_request_id: requestId,
        p_status: "failed",
        p_error_message: serviceError.message,
      });

      return { success: false, error: serviceError.message, code: "PROCESSING_FAILED", service: this.name, context: { params, error } };
    }
  }

  async collectUserData(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ServiceResponse<DSARExportData>> {
    logger.debug("Collecting user activity data", { userId, startDate, endDate });

    try {
      const { data, error } = await typedSupabase.rpc("collect_user_activity_data", {
        p_user_id: userId,
        p_start_date: startDate || null,
        p_end_date: endDate || null,
      });

      if (error) {
        return { success: false, error: error.message, code: "COLLECTION_FAILED", service: this.name, context: { userId, error } };
      }

      return { success: true, data: data as DSARExportData };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "COLLECTION_FAILED", service: this.name, context: { userId, error } };
    }
  }

  async getExportData(requestId: string): Promise<ServiceResponse<string>> {
    logger.debug("Fetching DSAR export data", { requestId });

    try {
      const requestResponse = await this.getRequest(requestId);
      if (!requestResponse.success || !requestResponse.data) {
        return { success: false, error: "DSAR request not found", code: "REQUEST_NOT_FOUND", service: this.name, context: { requestId } };
      }

      const request = requestResponse.data;

      if (request.status !== "completed") {
        return { success: false, error: "Export is not ready yet", code: "EXPORT_NOT_READY", service: this.name, context: { requestId, status: request.status } };
      }

      if (!request.export_file_url) {
        return { success: false, error: "Export file not found", code: "EXPORT_NOT_FOUND", service: this.name, context: { requestId } };
      }

      // Check if export has expired
      if (request.export_expires_at && new Date(request.export_expires_at) < new Date()) {
        return { success: false, error: "Export has expired", code: "EXPORT_EXPIRED", service: this.name, context: { requestId } };
      }

      // In production, this would fetch from S3
      // For now, return a placeholder
      const exportUrl = request.export_file_url;

      return { success: true, data: exportUrl };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { requestId, error } };
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const dsarService = new DSARService();
